// One Markdown/sentence walk for the HTTP source and browser feeder. No intent routing.
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';

const parser = unified().use(remarkParse).use(remarkGfm);
// voice-structural: Markdown block types whose visible text can be spoken
const TEXT_BLOCKS = new Set(['paragraph', 'heading']);
// voice-structural: structural Markdown nodes replaced by localized audio cues
const CUES = Object.freeze({
  table: 'tableOnScreen',
  code: 'codeOnScreen',
});
// voice-structural: Unicode sentence terminators, not words or commands
const TERMINATORS = new Set(Array.from('.!?。！？…'));
// voice-structural: closing punctuation allowed after a sentence terminator
const CLOSERS = new Set(Array.from('"\'”’»)]}'));

function ended(text) {
  const chars = Array.from(text.trimEnd());
  while (CLOSERS.has(chars.at(-1))) chars.pop();
  return TERMINATORS.has(chars.at(-1));
}

function inlineText(node, blocks) {
  let text = '';
  let linked = false;
  const visit = (child) => {
    if (CUES[child.type]) {
      if (text.trim()) blocks.push({ text });
      blocks.push({ cue: CUES[child.type], text: '' });
      text = '';
    } else if (child.type === 'link') {
      linked = true;
      // A descriptive label remains readable; a raw/autolink stays on screen.
      if (!(child.children?.length === 1 && child.children[0].value === child.url))
        for (const nested of child.children ?? []) visit(nested);
    } else if (child.type === 'text' || child.type === 'inlineCode') text += child.value;
    else if (child.type === 'break') text += ' ';
    else if (child.type !== 'html' && child.type !== 'image')
      for (const nested of child.children ?? []) visit(nested);
  };
  for (const child of node.children ?? []) visit(child);
  if (text.trim()) blocks.push({ text });
  if (linked) blocks.push({ cue: 'linkOnScreen', text: '' });
}

function walk(node, blocks) {
  if (CUES[node.type]) blocks.push({ cue: CUES[node.type], text: '' });
  else if (TEXT_BLOCKS.has(node.type)) inlineText(node, blocks);
  else for (const child of node.children ?? []) walk(child, blocks);
}

function sentences(text) {
  const parts = [...new Intl.Segmenter('und', { granularity: 'sentence' }).segment(text)];
  const result = [];
  let pending = null;
  for (const part of parts) {
    const current = pending
      ? { index: pending.index, segment: pending.segment + part.segment }
      : part;
    const words = [
      ...new Intl.Segmenter('und', { granularity: 'word' }).segment(part.segment),
    ].filter((word) => word.isWordLike);
    // ICU treats initials and titles as sentence ends. Hold single-word
    // period fragments with their following sentence instead of splitting a name.
    if (part.segment.trimEnd().endsWith('.') && words.length === 1) pending = current;
    else {
      result.push(current);
      pending = null;
    }
  }
  if (pending) result.push({ ...pending, held: true });
  return result;
}

/** BCP-47 normalization is structural; the app's interface language is never an input. */
export function speechLanguage(value) {
  if (!value) return null;
  try {
    return new Intl.Locale(value).language;
  } catch {
    return null;
  }
}

/** @returns {{text:string,cue?:string,start:number,end:number}[]} */
export function speakableSentences(markdown, { done = true } = {}) {
  const blocks = [];
  walk(parser.parse(String(markdown ?? '')), blocks);
  const result = [];
  let offset = 0;
  for (const [blockIndex, block] of blocks.entries()) {
    if (block.cue) {
      result.push({ ...block, start: offset, end: offset });
      continue;
    }
    const segments = sentences(block.text);
    for (const [index, part] of segments.entries()) {
      const last = blockIndex === blocks.length - 1 && index === segments.length - 1;
      if (
        !done &&
        last &&
        (part.held ||
          !ended(part.segment) ||
          !/\s$/u.test(part.segment + (last && /\s$/u.test(String(markdown)) ? ' ' : '')))
      )
        continue;
      const start = offset + part.index;
      if (part.segment.trim())
        result.push({
          text: part.segment.trim(), start, end: start + part.segment.length, block: blockIndex,
        });
    }
    offset += block.text.length;
  }
  return result;
}

export function isPlainSpeech(text) {
  const root = parser.parse(text);
  return (
    root.children.length > 0 &&
    root.children.every(
      (node) => node.type === 'paragraph' && node.children.every((child) => child.type === 'text'),
    )
  );
}
